import { Component, ElementRef, EventEmitter, Input, OnInit, OnChanges, Output, ViewChild, DestroyRef, inject, TemplateRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'rxjs/operators';
import { IIndicator } from 'src/app/_models/indicator.vm';
import { IndicatorNotification } from 'src/app/_models/indicatorNotification.vm';
import { ApiService } from 'src/app/_services/api.service';
import { GoogleAnalyticsService } from 'src/app/_services/google-analytics.service';
import { MetadataModalBooleanStoreService } from 'src/app/_services/metadataModalBooleanStore.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { trackByIndex } from '../utils/track-by';
import { ErrorReportingService } from '../../_services/error-reporting.service';

@Component({
    selector: 'app-indicator-metadata',
    templateUrl: './indicator-metadata.component.html',
    styleUrls: ['./indicator-metadata.component.scss'],
    standalone: false
})
export class IndicatorMetadataComponent implements OnInit {
  private readonly errorReporting = inject(ErrorReportingService);

  public readonly trackByIndex = trackByIndex;

  // Ties every subscription below to this component's lifetime, so a response
  // that arrives after the user has navigated away is dropped instead of
  // writing into a view that no longer exists.
  private readonly destroyRef = inject(DestroyRef);


  @ViewChild('metadataBtn', { static: true }) metadataBtn: ElementRef;
  @ViewChild('metaData', { static: true }) metaData!: TemplateRef<any>;
  @Input() indicator: IIndicator;
  @Input() isPathwayOpened: boolean;
  @Input() metadataWithinPathway?: boolean;
  @Output() isPathwayClosed= new EventEmitter<boolean>()
  @Input() isInTheModal?: boolean;

  public urlFragment: string
  public metadataModalBooleanStoreValue = this.metadataBooleanStoreService.getBooleanValue();
  public currentQueryParams;
  public urlIndicator;
  public indicatorMetaData: any = {};
  public indicatorAgeMetaData: any = {};
  public displayTable: any = {};
  public objectKeys = Object.keys;
  public isMetadataModalOpened: boolean = false;
  public openModals: NgbModalRef[] = [];
  public notifications: IndicatorNotification[]
  public indicatorNotificationCount: number
  public warningIconColor: string
  public isMetadataAvailable: boolean;

  constructor(
    private apiService: ApiService,
    private modalService: NgbModal,
    private router: Router,
    private route: ActivatedRoute,
    private googleAnalyticsService: GoogleAnalyticsService,
    private metadataBooleanStoreService: MetadataModalBooleanStoreService
  ) { }

  ngOnInit() {   
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.currentQueryParams = params;
    });
    this.route.fragment.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((fragment: string) => {
      this.urlFragment = fragment;
    })
    if (this.currentQueryParams.metadata && this.indicator.IndicatorID == this.currentQueryParams.metadata) {
      this.urlIndicator = this.indicator;
      this.openMetaData(this.metaData, this.urlIndicator)     
    }
    if (this.isInTheModal) {
      const metadataButton = this.metadataBtn.nativeElement;
      metadataButton.classList.add("metadata-modal-btn")
    }
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => { 
      this.checkQueryParams(); 
    }); 
    this.setNotificationWarningIcon()    
  }

  ngOnChanges() {
    this.setNotificationWarningIcon()
  }

  setNotificationWarningIcon() {
    this.indicatorNotificationCount = this.indicator.NotificationCount

    switch (this.indicator.HighestPriorityNotificationType) {
      case 'Red':
        this.warningIconColor = '#dc3545'
        break;
      case 'Blue':
        this.warningIconColor = '#007bff'
        break;
    }
  }

  getNotifications() {
    this.apiService.getIndicatorNotifications(this.indicator.IndicatorID,this.currentQueryParams.level,this.currentQueryParams.period).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      success => {
        this.notifications = success.notifications  
        const notificationDisplayOrder = ['Red', 'Blue'];
        this.notifications.sort((a, b) => {
          return notificationDisplayOrder.indexOf(a.NotificationTypeName) - notificationDisplayOrder.indexOf(b.NotificationTypeName);
        });
      },
      error => {
        this.errorReporting.report('Get notifications', error);
      }
    )
  }

  checkQueryParams() {
    const queryParams = this.route.snapshot.queryParams;
    if (this.isMetadataModalOpened && !queryParams.metadata) {
      this.dismissLastModal()
    }
  }

  changeTable(table) {
    if(table == 'age') this.displayTable = this.indicatorAgeMetaData;
    if(table == 'regular') this.displayTable = this.indicatorMetaData;
  }

  openMetaData(modal: TemplateRef<any>, indicator: IIndicator): void {
    this.getNotifications()
    let isModalOpened = this.metadataModalBooleanStoreValue.getValue()    
    if (!isModalOpened) {
      this.isMetadataModalOpened = true;
      if (this.indicator.IndicatorID.toString() != this.urlFragment &&this.isPathwayOpened &&!this.metadataWithinPathway) {      
        this.isPathwayClosed.emit()
        const queryParams = { ...this.route.snapshot.queryParams };   
        delete queryParams.pathway;  
        queryParams["metadata"] = this.indicator.IndicatorID
        this.router.navigate([], { fragment: this.indicator.IndicatorID.toString() , queryParams: queryParams});        
      } else {
        if (this.metadataWithinPathway) {
          this.router.navigate([], { queryParams: { metadata: this.indicator.IndicatorID, id: null }, preserveFragment:true, queryParamsHandling: 'merge' });
        } else {
          this.router.navigate([], { fragment: this.indicator.IndicatorID.toString() , queryParams: { metadata: this.indicator.IndicatorID, id: null }, queryParamsHandling: 'merge' });
        }
      }
      this.metadataBooleanStoreService.setBooleanValue(true);   
      this.apiService.getIndicatorDetails(indicator.IndicatorID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        success => {
          // Get meta data
          let indicatorMetaData = success.indicatorDetails.MetaData;

          if (indicatorMetaData && Object.keys(indicatorMetaData).length > 0) {
            this.isMetadataAvailable = true 
          }

          Object.keys(indicatorMetaData).forEach(section => {
            if(indicatorMetaData[section][0].AgeStandardised == 'Y') {
              this.indicatorAgeMetaData[section] =  indicatorMetaData[section];
            } else {
              this.indicatorMetaData[section] =  indicatorMetaData[section];
            }
          });

          this.displayTable = this.indicatorMetaData;

          // Collect data for Google Analytics
          if (this.isMetadataAvailable) {
            this.googleAnalyticsService.metadata("Metadata", "click", this.indicatorMetaData["Section 1: Indicator Overview & Rationale"][1].MetaData); 
          }
          // Open modal
          const modalRef= this.modalService.open(modal, { size: 'lg' });
          this.openModals.push(modalRef);

          // Subscribe to the result event to perform actions when the modal is closed
          modalRef.result.then((result) => { }).catch((reason) => {
          this.onClose()          
          }); 
        },
        error => {
          this.errorReporting.report('Open meta data', error);
        }
      )
    }     
  }

  onClose() {
    const queryParams = { ...this.route.snapshot.queryParams };   
    delete queryParams.metadata;  
    this.router.navigate([], { queryParams: queryParams });
    this.metadataBooleanStoreService.setBooleanValue(false); 
    this.isMetadataModalOpened = false;
  }

  dismissLastModal() {
    if (this.openModals.length > 0) {
      const lastModalRef = this.openModals.pop();
      lastModalRef.dismiss();
    }
  }

  downloadDataCollection() {
    this.googleAnalyticsService.downloadMetadata("Metadata Downloaded", "click", this.indicatorMetaData["Section 1: Indicator Overview & Rationale"][1].MetaData);
    window.location.href = 'https://api.cvdprevent.nhs.uk/indicator/' + this.indicator.IndicatorID + '/metaDataXLSX';
  }

  printDiv() {
    const printContents = document.getElementById('metadataContent').outerHTML;
    const popupWin = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
    if (popupWin) {
      popupWin.document.open();
      popupWin.document.write(`
        <html>
          <head>
            <title>Metadata</title>
            <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@4.0.0/dist/css/bootstrap.min.css" integrity="sha384-Gn5384xqQ1aoWXA+058RXPxPg6fy4IWvTNh0E263XmFcJlSAwiGgFAW/dAiS6JXm" crossorigin="anonymous">
          </head>
          <body>
            ${printContents}
          </body>
        </html>
      `);
      popupWin.document.close();
      setTimeout(() => {
        popupWin.focus();
        popupWin.print();
        popupWin.close();
      }, 500); 
    }
  }

}


